import { collection, addDoc, serverTimestamp, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { events, getPastEvents } from '../data/eventData';

// Helper function to convert date string to Date object
const parseEventDate = (dateStr, timeStr = '12:00 PM') => {
  try {
    // Combine date and time strings
    const dateTimeStr = `${dateStr} ${timeStr}`;
    const date = new Date(dateTimeStr);
    
    // If invalid date, try just the date string
    if (isNaN(date.getTime())) {
      return new Date(dateStr);
    }
    
    return date;
  } catch (error) {
    console.error('Error parsing date:', dateStr, timeStr, error);
    return new Date(); // Fallback to current date
  }
};

// Function to clean and prepare event data for Firebase
const prepareEventForFirebase = (event) => {
  const startDate = parseEventDate(event.date, event.time);
  
  // Calculate end date (default to 2 hours after start if not provided)
  let endDate = startDate;
  if (event.endTime) {
    endDate = parseEventDate(event.date, event.endTime);
  } else {
    endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // Add 2 hours
  }

  return {
    title: event.title || '',
    description: event.description || '',
    longDescription: event.longDescription || event.description || '',
    startDate: startDate,
    endDate: endDate,
    location: event.location || '',
    address: event.address || '',
    isVirtual: event.eventType === 'virtual' || event.location?.toLowerCase().includes('virtual') || false,
    virtualPlatform: event.virtualPlatform || (event.isVirtual ? 'Zoom' : ''),
    virtualLink: event.virtualLink || '',
    maxAttendees: event.maxAttendees || 100,
    eventType: event.eventType || 'Networking',
    featured: event.featured || false,
    image: event.image || '/images/events/event-placeholder.png',
    
    // Pricing information
    pricing: {
      member: event.memberPrice || 0,
      nonMember: event.nonMemberPrice || 25,
      sponsorDiscount: event.sponsorDiscount || 10
    },
    
    // Ticket types (if available)
    ticketTypes: event.ticketTypes || [
      {
        id: 'member',
        name: 'Member',
        price: event.memberPrice || 0,
        description: 'For APN members',
        memberOnly: true
      },
      {
        id: 'non-member',
        name: 'Non-Member',
        price: event.nonMemberPrice || 25,
        description: 'For non-members',
        memberOnly: false
      }
    ],
    
    // Sponsors (if available)
    sponsors: event.sponsors || [],
    
    // Registration and access dates
    registrationDeadline: event.registrationDeadline ? new Date(event.registrationDeadline) : new Date(startDate.getTime() - 24 * 60 * 60 * 1000), // 1 day before
    accessDates: event.accessDates || {
      basic: new Date(startDate.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 1 week before
      bronze: new Date(startDate.getTime() - 14 * 24 * 60 * 60 * 1000).toISOString(), // 2 weeks before
      silver: new Date(startDate.getTime() - 21 * 24 * 60 * 60 * 1000).toISOString(), // 3 weeks before
      gold: new Date(startDate.getTime() - 28 * 24 * 60 * 60 * 1000).toISOString() // 4 weeks before
    },
    
    // Additional fields
    agenda: event.agenda || '',
    speakers: event.speakers || [],
    requirements: event.requirements || '',
    attendees: event.attendees || [],
    registrations: [],
    
    // Status
    status: 'active',
    
    // Metadata
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    
    // Original ID for reference
    originalId: event.id
  };
};

// Function to clear existing events (for clean migration)
export const clearExistingEvents = async () => {
  try {
    console.log('🗑️ Clearing existing events...');
    const eventsRef = collection(db, 'events');
    const snapshot = await getDocs(eventsRef);

    const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    console.log(`✅ Cleared ${snapshot.size} existing events`);
    return snapshot.size;
  } catch (error) {
    console.error('❌ Error clearing events:', error);
    throw error;
  }
};

// Function to clear only test events (keep real events)
export const clearTestEvents = async () => {
  try {
    console.log('🗑️ Clearing test events...');
    const eventsRef = collection(db, 'events');
    const snapshot = await getDocs(eventsRef);

    const testEventIds = [3, 4, 5, 6, 7, 8]; // IDs of test events from 2025-2026
    const deletePromises = [];

    snapshot.docs.forEach(doc => {
      const eventData = doc.data();
      // Delete events with test IDs or future dates (2025-2026)
      if (testEventIds.includes(eventData.id) ||
          (eventData.date && (eventData.date.includes('2025') || eventData.date.includes('2026')))) {
        deletePromises.push(deleteDoc(doc.ref));
        console.log(`🗑️ Deleting test event: ${eventData.title}`);
      }
    });

    await Promise.all(deletePromises);

    console.log(`✅ Cleared ${deletePromises.length} test events`);
    return deletePromises.length;
  } catch (error) {
    console.error('❌ Error clearing test events:', error);
    throw error;
  }
};

// Function to migrate upcoming events
export const migrateUpcomingEvents = async () => {
  try {
    console.log('📅 Migrating upcoming events...');
    const eventsRef = collection(db, 'events');
    
    let successCount = 0;
    let errorCount = 0;
    
    for (const event of events) {
      try {
        const eventData = prepareEventForFirebase(event);
        await addDoc(eventsRef, eventData);
        console.log(`✅ Migrated upcoming event: ${event.title}`);
        successCount++;
      } catch (error) {
        console.error(`❌ Error migrating event ${event.title}:`, error);
        errorCount++;
      }
    }
    
    console.log(`📊 Upcoming events migration complete: ${successCount} success, ${errorCount} errors`);
    return { successCount, errorCount };
  } catch (error) {
    console.error('❌ Error migrating upcoming events:', error);
    throw error;
  }
};

// Function to migrate past events
export const migratePastEvents = async () => {
  try {
    console.log('📚 Migrating past events...');
    const eventsRef = collection(db, 'events');

    let successCount = 0;
    let errorCount = 0;

    const pastEvents = getPastEvents();
    for (const event of pastEvents) {
      try {
        const eventData = prepareEventForFirebase(event);
        // Mark past events as completed
        eventData.status = 'completed';
        
        await addDoc(eventsRef, eventData);
        console.log(`✅ Migrated past event: ${event.title}`);
        successCount++;
      } catch (error) {
        console.error(`❌ Error migrating event ${event.title}:`, error);
        errorCount++;
      }
    }
    
    console.log(`📊 Past events migration complete: ${successCount} success, ${errorCount} errors`);
    return { successCount, errorCount };
  } catch (error) {
    console.error('❌ Error migrating past events:', error);
    throw error;
  }
};

// Main migration function
export const migrateAllEvents = async (clearFirst = false) => {
  try {
    console.log('🚀 Starting events migration...');
    
    let clearedCount = 0;
    if (clearFirst) {
      clearedCount = await clearExistingEvents();
    }
    
    const upcomingResults = await migrateUpcomingEvents();
    const pastResults = await migratePastEvents();
    
    const totalSuccess = upcomingResults.successCount + pastResults.successCount;
    const totalErrors = upcomingResults.errorCount + pastResults.errorCount;
    
    console.log('🎉 Events migration complete!');
    console.log(`📊 Summary: ${totalSuccess} events migrated, ${totalErrors} errors, ${clearedCount} cleared`);
    
    return {
      cleared: clearedCount,
      migrated: totalSuccess,
      errors: totalErrors,
      upcoming: upcomingResults,
      past: pastResults
    };
  } catch (error) {
    console.error('❌ Events migration failed:', error);
    throw error;
  }
};
